import { useCallback, useMemo, useState } from 'react';

import type { CursorPages } from './useCursorPages';

/**
 * `useClientPages` — `useCursorPages`' shape over rows that are ALREADY in
 * memory, so `Pagination` can render for both kinds of list without knowing
 * which it is looking at.
 *
 * ## Why satisfy `CursorPages` rather than give `Pagination` a second mode
 *
 * Everything `Pagination` knows how to do — the disambiguated accessible
 * names, the `nav` landmark, the opaque `Surface` ground, the en-US grouping
 * of the range, the four page sizes — is work nobody wants a second copy of.
 * The only thing it actually reads from a `CursorPages` is `pageIndex`,
 * `perPage`, `canPrev`, `canNext` and the four commands, all of which are
 * answerable from a row count. `cursor` is `null` forever and `observe` is a
 * no-op: there is no server position to carry, which is precisely the
 * difference between the two hooks.
 *
 * ## `pageIndex` is CLAMPED on the way out, and the raw index is kept
 *
 * The row count moves under this hook constantly — a search box narrows 631
 * centres to 3 while the reader is on page 8. Clamping the index in state
 * would then LOSE page 8 when the query is cleared again; clamping only the
 * value that is read means the same reader lands back where they were. The
 * three commands operate on the clamped value, so `next()` from a stale
 * index 8 over a 3-row result cannot walk further into nothing.
 *
 * `setPerPage` resets to the first page, for the same reason `useCursorPages`
 * does: "rows 201-250" means nothing once a page is 100 rows long.
 */
export function useClientPages(total: number, initialPerPage = 10): CursorPages {
  const [{ index, perPage }, setState] = useState({ index: 0, perPage: initialPerPage });

  const lastIndex = Math.max(0, Math.ceil(total / perPage) - 1);
  const pageIndex = Math.min(index, lastIndex);

  const next = useCallback(() => {
    setState((s) => {
      const last = Math.max(0, Math.ceil(total / s.perPage) - 1);
      const current = Math.min(s.index, last);
      return current >= last ? s : { ...s, index: current + 1 };
    });
  }, [total]);

  const prev = useCallback(() => {
    setState((s) => {
      const last = Math.max(0, Math.ceil(total / s.perPage) - 1);
      const current = Math.min(s.index, last);
      return current <= 0 ? s : { ...s, index: current - 1 };
    });
  }, [total]);

  const first = useCallback(() => {
    setState((s) => (s.index === 0 ? s : { ...s, index: 0 }));
  }, []);

  const setPerPage = useCallback((n: number) => {
    setState((s) => (s.perPage === n ? s : { index: 0, perPage: n }));
  }, []);

  return useMemo(
    () => ({
      cursor: null,
      perPage,
      pageIndex,
      canPrev: pageIndex > 0,
      canNext: pageIndex < lastIndex,
      next,
      prev,
      first,
      setPerPage,
      observe: () => {
        // Nothing to observe: there is no server page to learn a cursor from.
      },
    }),
    [perPage, pageIndex, lastIndex, next, prev, first, setPerPage],
  );
}
